import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  PlusIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TableWithDetailCard } from '@/components/TableWithDetailCard/TableWithDetailCard';
import { FormTypeEnum } from '@/types';
import { getParamsFromTableFilters } from '@/utils/table-filters';

import { useLogos } from '../api/get-logos';
import { usePatchLogos } from '../api/patch-logos';
import { usePostLogos } from '../api/post-logos';
import { LogosDetailCard } from '../components/LogosDetailCard';
import { LogosDrawerForm } from '../components/LogosDrawerForm';
import { LogosTable } from '../components/LogosTable';
import { useLogosControllers } from '../hooks/useLogosControllers';
import { useLogosPermissions } from '../hooks/useLogosPermissions';
import { useLogosFilters } from '../stores/logos-filters';
import { TLogo } from '../types';

export default function LogosPage() {
  const { t } = useTranslation();

  const { filters, paginationValues, clearFilters } = useLogosFilters();

  /**
   * Resets table filters on page unmount.
   */
  useEffect(() => {
    return () => {
      clearFilters();
    };
  }, [clearFilters]);

  const {
    data: { data: logos = [], total: totalItems = 0, aggregation } = {},
    isFetching,
    isError,
  } = useLogos({
    params: getParamsFromTableFilters({
      filters,
      paginationValues,
    }),
  });

  const usersIds = Array.from(new Set([...(aggregation?.updated_by || [])]));

  const {
    logosDrawer,
    logosDialog,
    handleDeleteLogos,
    multiLogosDialog,
    handleDeleteMultipleLogos,
  } = useLogosControllers();

  const { mutate: createLogos, isPending: isCreatingLogos } = usePostLogos();

  const { mutate: updateLogos, isPending: isUpdatingLogos } = usePatchLogos();

  const { canCreate, canUpdate, canDuplicate, canDelete } = useLogosPermissions();

  const onCreate = () => {
    if (!canCreate) return;
    logosDrawer.open({ type: FormTypeEnum.CREATE, data: {} as TLogo });
  };

  const onUpdate = (logo: TLogo) => {
    if (!canUpdate) return;

    logosDrawer.open({ type: FormTypeEnum.UPDATE, data: logo });
  };

  const onDuplicate = (logo: TLogo) => {
    if (!canDuplicate) return;
    logosDrawer.open({ type: FormTypeEnum.DUPLICATE, data: logo });
  };

  const onDelete = (logo: TLogo) => {
    if (!canDelete) return;
    logosDialog.open(logo);
  };

  const onMultiDelete = (logos: TLogo[]) => {
    if (!canDelete) return;
    multiLogosDialog.open(logos);
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Logos')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              className="mr-size-16"
              onClick={onCreate}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: PlusIcon,
                },
                text: {
                  value: t('Nuevo'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>

        <TableWithDetailCard<TLogo>
          isError={isError}
          renderTable={({ setData, handleOpenSecondCard, data }) => (
            <LogosTable
              items={logos}
              usersIds={usersIds}
              isFetching={isFetching}
              activeItem={data}
              totalItems={totalItems}
              onUpdate={canUpdate ? onUpdate : undefined}
              onDuplicate={canDuplicate ? onDuplicate : undefined}
              onDelete={canDelete ? onDelete : undefined}
              onSelect={handleOpenSecondCard}
              onMultiDelete={canDelete ? onMultiDelete : undefined}
              setSelectedUser={setData}
            />
          )}
          renderDetailCard={({ data, isSecondCardCollapsed, handleSecondCardCollapse }) => (
            <LogosDetailCard
              logos={data}
              isCollapsed={isSecondCardCollapsed}
              onCollapse={handleSecondCardCollapse}
              onUpdate={onUpdate}
            />
          )}
        />
      </div>

      <LogosDrawerForm
        isOpen={logosDrawer.isOpen}
        onClose={logosDrawer.close}
        onSubmit={data => {
          if (logosDrawer.data?.type === FormTypeEnum.UPDATE) {
            updateLogos(
              {
                ...data,
                parameters: {
                  logos: data.logos_presigned_urls,
                  main_logo: typeof data.main_logo === 'string' ? data.main_logo : undefined,
                  include_in_clip_summary: data.parameters?.include_in_clip_summary,
                },
                id: logosDrawer.data.data.id,
                main_logo: data.main_logo instanceof File ? data.main_logo : undefined,
              },
              {
                onSuccess: () => {
                  logosDrawer.close();
                },
              }
            );
          } else {
            createLogos(
              {
                ...data,
                parameters: { ...data.parameters, logos_presigned_urls: data.logos_presigned_urls },
                state: data.state,
              },
              {
                onSuccess: () => {
                  logosDrawer.close();
                },
              }
            );
          }
        }}
        isMutating={isCreatingLogos || isUpdatingLogos}
        //@ts-ignore
        data={logosDrawer.data || undefined}
      />

      <ModalDialog
        isOpen={logosDialog.isOpen}
        onClose={logosDialog.close}
        onAction={handleDeleteLogos}
        heading={t('Eliminar logo')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage title={t('¿Estás seguro de querer eliminar el logo seleccionado?')} />
      </ModalDialog>

      <ModalDialog
        isOpen={multiLogosDialog.isOpen}
        onClose={multiLogosDialog.close}
        onAction={handleDeleteMultipleLogos}
        heading={t('Eliminar logos')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage title={t('¿Estás seguro de querer eliminar los logos seleccionados?')} />
      </ModalDialog>
    </>
  );
}
